import { ChevronRight, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { SubCategoryVM } from '../../store/stash-scoped/categorySelectors';

interface SubCategoryRowProps {
  sub: SubCategoryVM;
  onClick: () => void;
}

/**
 * One row in the Category Detail page's sub-category list.
 *
 * Layout: title (lead) + meta line (vendor preview / friends flag) on the left;
 * count badge + chevron on the right. No leading icon swatch — the title leads
 * the row per the spec. The empty variant (cardCount === 0) softens the row
 * (opacity-60), switches the badge to a dashed outline, and swaps the meta
 * line to an "add the first" hint; the row stays focusable and clickable so
 * tapping it routes to the same destination (empty card list).
 */
export function SubCategoryRow({ sub, onClick }: SubCategoryRowProps): React.JSX.Element {
  const isEmpty = sub.cardCount === 0;
  const metaLine = isEmpty
    ? 'Nothing stashed yet — add the first'
    : sub.previewVendorNames.join(', ');

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${sub.name}, ${sub.cardCount} cards`}
      className={cn(
        'group flex w-full items-center gap-3 rounded-[20px] border border-border bg-card px-4 py-3 text-left shadow-sm',
        'min-h-[64px] transition-all duration-120 ease-out',
        'hover:translate-x-0.5 hover:border-[var(--stash-blue)]/40 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        isEmpty && 'opacity-60',
      )}
    >
      {/* Title + meta line */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[17px] font-semibold leading-tight text-foreground">
          {sub.name}
        </p>
        <p className="mt-0.5 flex items-center gap-2 truncate text-xs text-muted-foreground">
          <span className="truncate">{metaLine}</span>
          {sub.friendCount > 0 && (
            <span className="inline-flex shrink-0 items-center gap-1 text-[var(--stash-blue)]">
              <Users size={12} strokeWidth={1.9} aria-hidden="true" />
              <span>{sub.friendCount} friends</span>
            </span>
          )}
        </p>
      </div>

      {/* Count badge */}
      <span
        className={cn(
          'inline-flex shrink-0 items-baseline gap-1.5 rounded-full px-3.5 py-2',
          isEmpty
            ? 'border border-dashed border-border bg-transparent text-muted-foreground'
            : 'bg-muted text-foreground transition-colors duration-120 group-hover:bg-[var(--stash-blue)] group-hover:text-white',
        )}
      >
        <span className="text-base font-bold">{sub.cardCount}</span>
        <span className="text-xs font-semibold">Cards</span>
      </span>

      {/* Trailing chevron */}
      <ChevronRight
        size={16}
        strokeWidth={1.9}
        aria-hidden="true"
        className="shrink-0 text-muted-foreground/70 transition-colors duration-120 group-hover:text-[var(--stash-blue)]"
      />
    </button>
  );
}
